import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { usePaywall } from "../components/PaywallModal";
import { handlePaywallResponse } from "../utils/paywallHelper";
import {
  Zap,
  ArrowLeft,
  Sparkles,
  Plus,
  X,
  Send,
  Copy,
  Check,
  Home,
  Save,
  FolderOpen,
  Trash2,
  MapPin,
  ChevronDown,
  Clock,
  Calendar,
  Sun,
  Moon,
  Sunrise
} from "lucide-react";
import { industries as FULL_INDUSTRIES, countries, statesByCountry, citiesByState } from "../data/formOptions";

// Use comprehensive industries from formOptions
const INDUSTRIES = FULL_INDUSTRIES;

const SENIORITY_LEVELS = [
  { value: "entry", label: "Entry Level" },
  { value: "mid", label: "Mid Level" },
  { value: "senior", label: "Senior Level" },
  { value: "lead", label: "Lead / Manager" },
  { value: "executive", label: "Executive" }
];

const EMPLOYMENT_TYPES = [
  { value: "full-time", label: "Full-Time" },
  { value: "part-time", label: "Part-Time" },
  { value: "contract", label: "Contract" },
  { value: "freelance", label: "Freelance" },
  { value: "remote", label: "Remote" },
  { value: "hybrid", label: "Hybrid" }
];

const CURRENCIES = [
  "USD", "EUR", "GBP", "CAD", "AUD", "INR", "SGD", "AED", "CHF", "JPY", "NGN"
];

const SALARY_PERIODS = [
  { value: "yearly", label: "Per Year" },
  { value: "monthly", label: "Per Month" },
  { value: "hourly", label: "Per Hour" }
];

const SHIFT_TYPES = [
  { value: "first", label: "First Shift (Morning)", description: "Typically 6 AM - 2 PM" },
  { value: "second", label: "Second Shift (Afternoon)", description: "Typically 2 PM - 10 PM" },
  { value: "third", label: "Third Shift (Night)", description: "Typically 10 PM - 6 AM" },
  { value: "rotating", label: "Rotating Shifts", description: "Varies weekly/monthly" },
  { value: "flexible", label: "Flexible Hours", description: "Choose your own hours" }
];

const WORK_DAYS = [
  { value: "monday", label: "Mon" },
  { value: "tuesday", label: "Tue" },
  { value: "wednesday", label: "Wed" },
  { value: "thursday", label: "Thu" },
  { value: "friday", label: "Fri" },
  { value: "saturday", label: "Sat" },
  { value: "sunday", label: "Sun" }
];

const JOB_FUNCTIONS = [
  "Accounting & Finance", "Administration", "Customer Service", "Data & Analytics",
  "Design & Creative", "Engineering", "Healthcare", "Human Resources",
  "IT & Technology", "Legal", "Logistics & Supply Chain", "Marketing",
  "Operations", "Product Management", "Project Management", "Quality Assurance",
  "Research & Development", "Sales & Business Development", "Security",
  "Strategy & Consulting", "Teaching & Education", "Writing & Content", "Other"
];

// Work hours dropdown options
const WORK_HOURS = [
  { value: "06:00", label: "6:00 AM" },
  { value: "06:30", label: "6:30 AM" },
  { value: "07:00", label: "7:00 AM" },
  { value: "07:30", label: "7:30 AM" },
  { value: "08:00", label: "8:00 AM" },
  { value: "08:30", label: "8:30 AM" },
  { value: "09:00", label: "9:00 AM" },
  { value: "09:30", label: "9:30 AM" },
  { value: "10:00", label: "10:00 AM" },
  { value: "10:30", label: "10:30 AM" },
  { value: "11:00", label: "11:00 AM" },
  { value: "11:30", label: "11:30 AM" },
  { value: "12:00", label: "12:00 PM" },
  { value: "12:30", label: "12:30 PM" },
  { value: "13:00", label: "1:00 PM" },
  { value: "13:30", label: "1:30 PM" },
  { value: "14:00", label: "2:00 PM" },
  { value: "14:30", label: "2:30 PM" },
  { value: "15:00", label: "3:00 PM" },
  { value: "15:30", label: "3:30 PM" },
  { value: "16:00", label: "4:00 PM" },
  { value: "16:30", label: "4:30 PM" },
  { value: "17:00", label: "5:00 PM" },
  { value: "17:30", label: "5:30 PM" },
  { value: "18:00", label: "6:00 PM" },
  { value: "18:30", label: "6:30 PM" },
  { value: "19:00", label: "7:00 PM" },
  { value: "19:30", label: "7:30 PM" },
  { value: "20:00", label: "8:00 PM" },
  { value: "20:30", label: "8:30 PM" },
  { value: "21:00", label: "9:00 PM" },
  { value: "21:30", label: "9:30 PM" },
  { value: "22:00", label: "10:00 PM" },
  { value: "22:30", label: "10:30 PM" },
  { value: "23:00", label: "11:00 PM" },
  { value: "23:30", label: "11:30 PM" },
  { value: "00:00", label: "12:00 AM (Midnight)" }
];

const JobCreator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showPaywall } = usePaywall();
  const [currencies, setCurrencies] = useState(CURRENCIES);
  const [jobData, setJobData] = useState({
    title: "",
    industry: "",
    seniority_level: "mid",
    employment_type: "full-time",
    skills: [],
    location: "",
    description: "",
    salary_min: "",
    salary_max: "",
    salary_currency: "USD",
    salary_period: "yearly",
    // New shift/timing fields
    shift_type: "",
    work_timing_start: "",
    work_timing_end: "",
    work_days: [],
    is_urgent: false,
    job_function: ""
  });
  const [newSkill, setNewSkill] = useState("");
  const [generating, setGenerating] = useState(false);
  const [posting, setPosting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Location state
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [isRemote, setIsRemote] = useState(false);
  const [availableStates, setAvailableStates] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  
  // Industry search
  const [industrySearch, setIndustrySearch] = useState("");
  const [showIndustryDropdown, setShowIndustryDropdown] = useState(false);
  
  // Update available states when country changes
  useEffect(() => {
    if (selectedCountry && statesByCountry[selectedCountry]) {
      setAvailableStates(statesByCountry[selectedCountry]);
    } else {
      setAvailableStates([]);
    }
    setSelectedState("");
    setSelectedCity("");
    setAvailableCities([]);
  }, [selectedCountry]);
  
  // Update available cities when state changes
  useEffect(() => {
    if (selectedCountry && selectedState && citiesByState[selectedCountry]?.[selectedState]) {
      setAvailableCities(citiesByState[selectedCountry][selectedState]);
    } else {
      setAvailableCities([]);
    }
    setSelectedCity("");
  }, [selectedCountry, selectedState]);
  
  // Update location string when any location part changes
  useEffect(() => {
    if (isRemote) {
      setJobData(prev => ({ ...prev, location: "Remote" }));
    } else {
      const parts = [selectedCity, selectedState, selectedCountry].filter(Boolean);
      const locationString = parts.join(", ");
      setJobData(prev => ({ ...prev, location: locationString }));
    }
  }, [selectedCountry, selectedState, selectedCity, isRemote]);
  
  // Filter industries based on search
  const filteredIndustries = INDUSTRIES.filter(ind => 
    ind.toLowerCase().includes(industrySearch.toLowerCase())
  );

  useEffect(() => {
    fetchCurrencies();
    fetchTemplates();
  }, []);

  const fetchCurrencies = async () => {
    try {
      const response = await fetch(`${API}/currencies`);
      if (response.ok) {
        const data = await response.json();
        if (data.currencies) {
          setCurrencies(data.currencies);
        }
      }
    } catch (error) {
      console.error("Failed to fetch currencies:", error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API}/templates/job`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error("Please enter a template name");
      return;
    }

    setSavingTemplate(true);
    try {
      const response = await fetch(`${API}/templates/job`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: templateName,
          ...jobData,
          salary_min: jobData.salary_min ? parseFloat(jobData.salary_min) : null,
          salary_max: jobData.salary_max ? parseFloat(jobData.salary_max) : null,
        }),
      });

      if (response.ok) {
        toast.success("Template saved!");
        setShowSaveDialog(false);
        setTemplateName("");
        fetchTemplates();
      } else {
        toast.error("Failed to save template");
      }
    } catch (error) {
      toast.error("Failed to save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleLoadTemplate = (template) => {
    setJobData({
      title: template.title || "",
      industry: template.industry || "",
      seniority_level: template.seniority_level || "mid",
      employment_type: template.employment_type || "full-time",
      skills: template.skills || [],
      location: template.location || "",
      description: template.description || "",
      salary_min: template.salary_min?.toString() || "",
      salary_max: template.salary_max?.toString() || "",
      salary_currency: template.salary_currency || "USD"
    });
    setShowTemplates(false);
    toast.success(`Loaded template: ${template.name}`);
  };

  const handleDeleteTemplate = async (templateId, e) => {
    e.stopPropagation();
    try {
      const response = await fetch(`${API}/templates/job/${templateId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Template deleted");
        fetchTemplates();
      } else {
        toast.error("Failed to delete template");
      }
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const handleGenerateDescription = async () => {
    if (!jobData.title || !jobData.industry) {
      toast.error("Please enter job title and industry");
      return;
    }

    setGenerating(true);
    try {
      const response = await fetch(`${API}/ai/generate-job-description`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(jobData),
      });

      if (response.ok) {
        const data = await response.json();
        setJobData(prev => ({ ...prev, description: data.description }));
        toast.success("Job description generated!");
      } else {
        if (await handlePaywallResponse(response, showPaywall)) return;
        toast.error("Failed to generate description");
      }
    } catch (error) {
      toast.error("Failed to generate description");
    } finally {
      setGenerating(false);
    }
  };

  const handlePostJob = async () => {
    if (!jobData.title || !jobData.industry || !jobData.description) {
      toast.error("Please fill in all required fields");
      return;
    }

    setPosting(true);
    try {
      const response = await fetch(`${API}/jobs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...jobData,
          salary_min: jobData.salary_min ? parseFloat(jobData.salary_min) : null,
          salary_max: jobData.salary_max ? parseFloat(jobData.salary_max) : null,
        }),
      });

      if (response.ok) {
        const job = await response.json();
        toast.success("Job posted successfully!");
        navigate(`/jobs/${job.job_id}`);
      } else {
        if (await handlePaywallResponse(response, showPaywall)) return;
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Failed to post job");
      }
    } catch (error) {
      toast.error("Failed to post job");
    } finally {
      setPosting(false);
    }
  };

  const addSkill = () => {
    if (newSkill.trim() && !jobData.skills.includes(newSkill.trim())) {
      setJobData(prev => ({ ...prev, skills: [...prev.skills, newSkill.trim()] }));
      setNewSkill("");
    }
  };

  const removeSkill = (skill) => {
    setJobData(prev => ({ ...prev, skills: prev.skills.filter(s => s !== skill) }));
  };

  const copyDescription = () => {
    navigator.clipboard.writeText(jobData.description);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Description copied!");
  };

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />
      
      <main className="lg:ml-64 min-h-screen p-4 sm:p-6 pt-20 lg:pt-8">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            className="mb-6 sm:mb-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">
              CREATE <span className="text-crimson">JOB POSTING</span>
            </h1>
            <p className="text-gray-400 text-sm sm:text-base">Generate professional job descriptions in seconds with AI</p>
            
            {/* Template Buttons */}
            <div className="flex gap-3 mt-4">
              <Button
                onClick={() => setShowTemplates(!showTemplates)}
                variant="outline"
                className="border-[#333] hover:border-crimson text-white"
                data-testid="load-template-btn"
              >
                <FolderOpen className="w-4 h-4 mr-2" /> 
                Load Template {templates.length > 0 && `(${templates.length})`}
              </Button>
              <Button
                onClick={() => setShowSaveDialog(true)}
                variant="outline"
                className="border-[#333] hover:border-crimson text-white"
                data-testid="save-template-btn"
              >
                <Save className="w-4 h-4 mr-2" /> Save as Template
              </Button>
            </div>
          </motion.div>

          {/* Templates Panel */}
          <AnimatePresence>
            {showTemplates && templates.length > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-8 overflow-hidden"
              >
                <div className="bg-obsidian-light border border-[#222] p-4">
                  <h3 className="font-heading text-lg font-bold mb-4">SAVED TEMPLATES</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {templates.map((template) => (
                      <div
                        key={template.template_id}
                        onClick={() => handleLoadTemplate(template)}
                        className="p-4 bg-obsidian border border-[#333] hover:border-crimson cursor-pointer transition-colors group"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-bold text-white">{template.name}</h4>
                            <p className="text-sm text-gray-500">{template.title || "Untitled"}</p>
                            <p className="text-xs text-gray-600 mt-1">
                              {template.industry} • {template.employment_type}
                            </p>
                          </div>
                          <Button
                            onClick={(e) => handleDeleteTemplate(template.template_id, e)}
                            variant="ghost"
                            size="sm"
                            className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-crimson"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Save Template Dialog */}
          <AnimatePresence>
            {showSaveDialog && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
                onClick={() => setShowSaveDialog(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="bg-obsidian-light border border-[#222] p-6 max-w-md w-full mx-4"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="font-heading text-xl font-bold mb-4">SAVE TEMPLATE</h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                        Template Name *
                      </Label>
                      <Input
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                        placeholder="e.g., Senior Developer Template"
                        className="bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                        data-testid="template-name-input"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={() => setShowSaveDialog(false)}
                        variant="outline"
                        className="flex-1 border-[#333] text-white"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSaveTemplate}
                        disabled={savingTemplate || !templateName.trim()}
                        className="flex-1 bg-crimson hover:bg-crimson-hover text-white"
                        data-testid="confirm-save-template-btn"
                      >
                        {savingTemplate ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          "Save Template"
                        )}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Form */}
            <motion.div 
              className="space-y-6"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="bg-obsidian-light border border-[#222] p-6">
                <h3 className="font-heading text-lg font-bold mb-4">JOB DETAILS</h3>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Job Title *
                    </Label>
                    <Input
                      value={jobData.title}
                      onChange={(e) => setJobData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Senior Frontend Developer"
                      className="bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                      data-testid="job-title-input"
                    />
                  </div>

                  <div className="relative">
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Industry *
                    </Label>
                    <div className="relative">
                      <Input
                        value={industrySearch || jobData.industry}
                        onChange={(e) => {
                          setIndustrySearch(e.target.value);
                          setShowIndustryDropdown(true);
                        }}
                        onFocus={() => setShowIndustryDropdown(true)}
                        placeholder="Search or select industry..."
                        className="bg-obsidian border-[#333] text-white placeholder:text-gray-600 pr-10"
                        data-testid="industry-select"
                      />
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                    </div>
                    <AnimatePresence>
                      {showIndustryDropdown && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute z-50 w-full mt-1 bg-obsidian border border-[#333] max-h-60 overflow-y-auto"
                        >
                          {filteredIndustries.length > 0 ? (
                            filteredIndustries.map(ind => (
                              <div
                                key={ind}
                                onClick={() => {
                                  setJobData(prev => ({ ...prev, industry: ind }));
                                  setIndustrySearch("");
                                  setShowIndustryDropdown(false);
                                }}
                                className={`px-3 py-2 cursor-pointer hover:bg-crimson/20 transition-colors ${
                                  jobData.industry === ind ? "bg-crimson/10 text-crimson" : "text-white"
                                }`}
                              >
                                {ind}
                              </div>
                            ))
                          ) : (
                            <div className="px-3 py-2 text-gray-500">No industries found</div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                    {/* Click outside to close */}
                    {showIndustryDropdown && (
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setShowIndustryDropdown(false)}
                      />
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                        Seniority Level
                      </Label>
                      <select
                        value={jobData.seniority_level}
                        onChange={(e) => setJobData(prev => ({ ...prev, seniority_level: e.target.value }))}
                        className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white focus:border-crimson focus:ring-1 focus:ring-crimson"
                        data-testid="seniority-select"
                      >
                        {SENIORITY_LEVELS.map(level => (
                          <option key={level.value} value={level.value}>{level.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                        Employment Type
                      </Label>
                      <select
                        value={jobData.employment_type}
                        onChange={(e) => setJobData(prev => ({ ...prev, employment_type: e.target.value }))}
                        className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white focus:border-crimson focus:ring-1 focus:ring-crimson"
                        data-testid="employment-type-select"
                      >
                        {EMPLOYMENT_TYPES.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Job Function & Urgent */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                        Job Function
                      </Label>
                      <select
                        value={jobData.job_function}
                        onChange={(e) => setJobData(prev => ({ ...prev, job_function: e.target.value }))}
                        className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white focus:border-crimson focus:ring-1 focus:ring-crimson"
                        data-testid="job-function-select"
                      >
                        <option value="">Select Function</option>
                        {JOB_FUNCTIONS.map(fn => (
                          <option key={fn} value={fn}>{fn}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-3 cursor-pointer h-10 px-3 bg-obsidian border border-[#333] rounded w-full">
                        <input
                          type="checkbox"
                          checked={jobData.is_urgent}
                          onChange={(e) => setJobData(prev => ({ ...prev, is_urgent: e.target.checked }))}
                          className="w-4 h-4 accent-crimson"
                          data-testid="urgent-checkbox"
                        />
                        <span className="text-sm text-gray-300">Urgently Hiring</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Location
                    </Label>
                    
                    {/* Remote Toggle */}
                    <div className="flex items-center gap-3 mb-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isRemote}
                          onChange={(e) => setIsRemote(e.target.checked)}
                          className="w-4 h-4 accent-crimson"
                          data-testid="remote-checkbox"
                        />
                        <span className="text-sm text-gray-300">Remote Position</span>
                      </label>
                    </div>
                    
                    {!isRemote && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Country */}
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Country</label>
                          <select
                            value={selectedCountry}
                            onChange={(e) => setSelectedCountry(e.target.value)}
                            className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white focus:border-crimson focus:ring-1 focus:ring-crimson"
                            data-testid="country-select"
                          >
                            <option value="">Select Country</option>
                            {countries.map(country => (
                              <option key={country} value={country}>{country}</option>
                            ))}
                          </select>
                        </div>
                        
                        {/* State/Region */}
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">State/Region</label>
                          <select
                            value={selectedState}
                            onChange={(e) => setSelectedState(e.target.value)}
                            className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white focus:border-crimson focus:ring-1 focus:ring-crimson disabled:opacity-50"
                            disabled={!selectedCountry || availableStates.length === 0}
                            data-testid="state-select"
                          >
                            <option value="">{availableStates.length > 0 ? "Select State/Region" : "Select country first"}</option>
                            {availableStates.map(state => (
                              <option key={state} value={state}>{state}</option>
                            ))}
                          </select>
                        </div>
                        
                        {/* City */}
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">City</label>
                          <select
                            value={selectedCity}
                            onChange={(e) => setSelectedCity(e.target.value)}
                            className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white focus:border-crimson focus:ring-1 focus:ring-crimson disabled:opacity-50"
                            disabled={!selectedState || availableCities.length === 0}
                            data-testid="city-select"
                          >
                            <option value="">{availableCities.length > 0 ? "Select City" : "Select state first"}</option>
                            {availableCities.map(city => (
                              <option key={city} value={city}>{city}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                    
                    {/* Location Preview */}
                    {jobData.location && (
                      <div className="mt-2 text-sm text-gray-400 flex items-center gap-2">
                        <MapPin className="w-3 h-3 text-crimson" />
                        <span>{jobData.location}</span>
                      </div>
                    )}
                  </div>

                  {/* Salary Section - Better Layout */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                          Currency
                        </Label>
                        <select
                          value={jobData.salary_currency}
                          onChange={(e) => setJobData(prev => ({ ...prev, salary_currency: e.target.value }))}
                          className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white focus:border-crimson focus:ring-1 focus:ring-crimson"
                          data-testid="currency-select"
                        >
                          {currencies.map(curr => (
                            <option key={curr} value={curr}>{curr}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                          Period
                        </Label>
                        <select
                          value={jobData.salary_period || "yearly"}
                          onChange={(e) => setJobData(prev => ({ ...prev, salary_period: e.target.value }))}
                          className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white focus:border-crimson focus:ring-1 focus:ring-crimson"
                          data-testid="salary-period-select"
                        >
                          {SALARY_PERIODS.map(period => (
                            <option key={period.value} value={period.value}>{period.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                          Minimum Salary
                        </Label>
                        <Input
                          type="number"
                          value={jobData.salary_min}
                          onChange={(e) => setJobData(prev => ({ ...prev, salary_min: e.target.value }))}
                          placeholder="e.g., 80000"
                          className="bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                          data-testid="salary-min-input"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                          Maximum Salary
                        </Label>
                        <Input
                          type="number"
                          value={jobData.salary_max}
                          onChange={(e) => setJobData(prev => ({ ...prev, salary_max: e.target.value }))}
                          placeholder="e.g., 120000"
                          className="bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                          data-testid="salary-max-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Work Schedule Section */}
              <div className="bg-obsidian-light border border-[#222] p-6">
                <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-crimson" /> WORK SCHEDULE
                </h3>
                
                {/* Shift Type */}
                <div className="mb-4">
                  <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                    Shift Type
                  </Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {SHIFT_TYPES.map(shift => (
                      <div
                        key={shift.value}
                        onClick={() => setJobData(prev => ({ ...prev, shift_type: shift.value }))}
                        className={`p-3 border cursor-pointer transition-all ${
                          jobData.shift_type === shift.value 
                            ? "border-crimson bg-crimson/10" 
                            : "border-[#333] hover:border-[#444]"
                        }`}
                        data-testid={`shift-${shift.value}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          {shift.value === "first" && <Sunrise className="w-4 h-4 text-yellow-400" />}
                          {shift.value === "second" && <Sun className="w-4 h-4 text-orange-400" />}
                          {shift.value === "third" && <Moon className="w-4 h-4 text-blue-400" />}
                          {shift.value === "rotating" && <Clock className="w-4 h-4 text-purple-400" />}
                          {shift.value === "flexible" && <Calendar className="w-4 h-4 text-green-400" />}
                          <span className="text-sm font-medium text-white">{shift.label}</span>
                        </div>
                        <p className="text-xs text-gray-500">{shift.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Work Timing */}
                <div className="mb-4">
                  <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                    Work Hours (Optional)
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Start Time</label>
                      <select
                        value={jobData.work_timing_start}
                        onChange={(e) => setJobData(prev => ({ ...prev, work_timing_start: e.target.value }))}
                        className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white focus:border-crimson focus:ring-1 focus:ring-crimson"
                        data-testid="work-timing-start"
                      >
                        <option value="">Select Start Time</option>
                        {WORK_HOURS.map(hour => (
                          <option key={`start-${hour.value}`} value={hour.value}>{hour.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">End Time</label>
                      <select
                        value={jobData.work_timing_end}
                        onChange={(e) => setJobData(prev => ({ ...prev, work_timing_end: e.target.value }))}
                        className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white focus:border-crimson focus:ring-1 focus:ring-crimson"
                        data-testid="work-timing-end"
                      >
                        <option value="">Select End Time</option>
                        {WORK_HOURS.map(hour => (
                          <option key={`end-${hour.value}`} value={hour.value}>{hour.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Work Days */}
                <div>
                  <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                    Work Days
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {WORK_DAYS.map(day => (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() => {
                          const currentDays = jobData.work_days || [];
                          const newDays = currentDays.includes(day.value)
                            ? currentDays.filter(d => d !== day.value)
                            : [...currentDays, day.value];
                          setJobData(prev => ({ ...prev, work_days: newDays }));
                        }}
                        className={`px-4 py-2 border transition-all font-medium text-sm ${
                          (jobData.work_days || []).includes(day.value)
                            ? "border-crimson bg-crimson/20 text-crimson"
                            : "border-[#333] text-gray-400 hover:border-[#444]"
                        }`}
                        data-testid={`day-${day.value}`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      onClick={() => setJobData(prev => ({ 
                        ...prev, 
                        work_days: ["monday", "tuesday", "wednesday", "thursday", "friday"] 
                      }))}
                      className="text-xs text-crimson hover:underline"
                    >
                      Weekdays
                    </button>
                    <button
                      type="button"
                      onClick={() => setJobData(prev => ({ 
                        ...prev, 
                        work_days: ["saturday", "sunday"] 
                      }))}
                      className="text-xs text-crimson hover:underline"
                    >
                      Weekends
                    </button>
                    <button
                      type="button"
                      onClick={() => setJobData(prev => ({ 
                        ...prev, 
                        work_days: WORK_DAYS.map(d => d.value) 
                      }))}
                      className="text-xs text-crimson hover:underline"
                    >
                      All Days
                    </button>
                  </div>
                </div>
              </div>

              {/* Skills */}
              <div className="bg-obsidian-light border border-[#222] p-6">
                <h3 className="font-heading text-lg font-bold mb-4">KEY SKILLS</h3>
                
                <div className="flex gap-2 mb-4">
                  <Input
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addSkill()}
                    placeholder="Add required skill"
                    className="bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                    data-testid="skill-input"
                  />
                  <Button
                    onClick={addSkill}
                    className="bg-crimson hover:bg-crimson-hover text-white"
                    data-testid="add-skill-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {jobData.skills.map((skill, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-1 bg-crimson/10 border border-crimson/30 text-crimson"
                    >
                      {skill}
                      <button onClick={() => removeSkill(skill)} className="hover:text-white">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Generate Button */}
              <Button
                onClick={handleGenerateDescription}
                disabled={generating || !jobData.title || !jobData.industry}
                className="w-full bg-gradient-to-r from-crimson to-crimson-hover hover:from-crimson-hover hover:to-crimson text-white font-bold uppercase tracking-wider py-6"
                data-testid="generate-description-btn"
              >
                {generating ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" /> Generate AI Description
                  </>
                )}
              </Button>
            </motion.div>

            {/* Right Column - Preview */}
            <motion.div 
              className="bg-obsidian-light border border-[#222] p-6"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-bold">JOB DESCRIPTION</h3>
                {jobData.description && (
                  <Button
                    onClick={copyDescription}
                    variant="ghost"
                    size="sm"
                    className="text-gray-400 hover:text-white"
                    data-testid="copy-description-btn"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                )}
              </div>

              {jobData.description ? (
                <div className="prose prose-invert prose-sm max-w-none">
                  <Textarea
                    value={jobData.description}
                    onChange={(e) => setJobData(prev => ({ ...prev, description: e.target.value }))}
                    className="bg-obsidian border-[#333] text-white min-h-[500px] font-mono text-sm"
                    data-testid="description-textarea"
                  />
                </div>
              ) : (
                <div className="h-[500px] flex items-center justify-center border border-dashed border-[#333]">
                  <div className="text-center">
                    <Sparkles className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-500">
                      Fill in job details and click<br />
                      &quot;Generate AI Description&quot;
                    </p>
                  </div>
                </div>
              )}

              {jobData.description && (
                <Button
                  onClick={handlePostJob}
                  disabled={posting}
                  className="w-full mt-4 bg-crimson hover:bg-crimson-hover text-white font-bold uppercase tracking-wider py-6"
                  data-testid="post-job-btn"
                >
                  {posting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Posting...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" /> Post Job
                    </>
                  )}
                </Button>
              )}
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default JobCreator;
